import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { api } from '../../lib/api'

/** The till's wire types and hooks. Prices always come from the server's quote. */

export type PaymentMode = 'Cash' | 'Mpesa' | 'EquityBank' | 'SidianBank' | 'DtbBank' | 'AbsaBank' | 'Card' | 'CreditSale' | 'Cheque' | 'ExchangeCredit' | 'LoyaltyPoints'

export type PosLocation = { id: string; code: string; name: string }

export type PosCustomer = {
  id: string
  code: string
  name: string
  type: string
  priceTier: string
  phone?: string
  email?: string
  isWalkIn: boolean
  creditLimit: number
  balance: number
  availableCredit: number
  isBlocked: boolean
  vatExemptionCertificate?: string
  vatExemptionExpiresOn?: string
  /** A corporate account that buys on account only against its own LPO. */
  requiresPurchaseOrder?: boolean
}

export type RegisterSession = {
  id: string
  locationId: string
  cashierName: string
  status: 'Open' | 'Closed'
  openedAt: string
  closedAt?: string
  openingFloat: number
  /** Absent for a cashier while the session is open: the count at close is blind. */
  cashSales?: number
  cashPaidOut?: number
  expectedCash?: number
  mpesaSales: number
  cardSales: number
  bankSales: number
  creditSales: number
  totalTakings: number
  salesCount: number
  countedCash?: number
  variance?: number
  /** Which counter this shift is on. Absent where the shop names no counters (D-192). */
  counterName?: string
}

export type PosContext = {
  location: PosLocation
  walkIn: PosCustomer
  session?: RegisterSession
  pricesIncludeVat: boolean
  vatRate: number
  /** What this cashier may discount unaided at this shop — the shop's line, not their role's (D-240). */
  maxDiscountPercent?: number | null
  /** Above this, nobody but the owner can approve a discount here. */
  managerDiscountCeilingPercent?: number
  /** The shop sells into negative stock; the till then sets no limit on quantities. */
  allowNegativeStock?: boolean
  /** The shop's counters, for the cashier to pick from. Absent or empty where it names none. */
  counters?: PosCounter[]
  /** How long a parked sale holds its stock unless the cashier chooses otherwise (D-212). */
  parkedSaleHoldHours?: number
  /** The holds the till may offer. The server owns the list. */
  parkedSaleHoldChoices?: number[]
}

/** Somewhere else a product is, when this shop is short (D-193). */
export type StockElsewhere = {
  locationId: string
  code: string
  name: string
  isWarehouse: boolean
  available: number
  /** Null means "we do not know how far", not "far" — that shop has no pin on the map. */
  distanceKm?: number
  /** Whether that one place covers the whole shortfall on its own. */
  enough: boolean
}

export type CollectionOrderResult = {
  orderId: string
  orderNumber: string
  fromLocationName: string
  quantity: number
  indentCode: string
  /** Absent until the far shop agrees — nothing is held before then. */
  holdExpiresAt?: string
}

/**
 * One account on the shop's day book (D-195).
 *
 * `book` is what the ledger says and is never typed. `declared` is what somebody actually saw —
 * absent means nobody has looked, which is not the same as zero.
 */
export type DayBookLine = {
  accountId: string
  code: string
  name: string
  /** The company's rather than this shop's: the bank, and the paybill where the shop has no till. */
  shared: boolean
  book: number
  declared?: number
  /** Declared less book. Negative is short. */
  difference?: number
}

export type ShopDay = {
  locationId: string
  businessDate: string
  status: 'NotOpened' | 'Open' | 'Closed'
  openedAt?: string
  openedBy?: string
  closedAt?: string
  closedBy?: string
  opening: DayBookLine[]
  closing: DayBookLine[]
  /** Withheld until the day is open, unless you may manage it — see the endpoint's remarks. */
  yesterdayDate?: string
  yesterday: DayBookLine[]
  mayManage: boolean
}

export function useShopDay(locationId: string) {
  return useQuery({
    queryKey: ['pos', 'day', locationId],
    queryFn: ({ signal }) => api<ShopDay>(`/api/pos/day?locationId=${locationId}`, { signal }),
  })
}

export function useOpenDay(locationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { declared: Record<string, number>; note?: string }) =>
      api<ShopDay>('/api/pos/day/open', { method: 'POST', body: { locationId, ...body } }),
    onSuccess: (day) => client.setQueryData(['pos', 'day', locationId], day),
  })
}

export function useCloseDay(locationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { declared: Record<string, number>; note?: string; finish: boolean }) =>
      api<ShopDay>('/api/pos/day/close', { method: 'POST', body: { locationId, ...body } }),
    onSuccess: (day) => client.setQueryData(['pos', 'day', locationId], day),
  })
}

/** A sale parked part-way, waiting for its customer (D-197). */
export type ParkedSale = {
  id: string
  /** What the cashier called it — a name, a phone, "blue shirt". How it is found again. */
  reference: string
  /** Whole, not by name: picking it up puts this customer back on the till with their tier and credit. */
  customer: PosCustomer
  parkedBy: string
  parkedAt: string
  expiresAt: string
  /** When the stock behind it goes back on the shelf. Much sooner than the sale expires. */
  stockHeldUntil: string
  /** False means the lines are re-checked when it is picked up. */
  stockStillHeld: boolean
  lineCount: number
  /** In pieces, so a box of six reads as six things somebody is waiting for. */
  totalQuantity: number
  lines: ParkedSaleLine[]
  /** How long the stock was asked to be held for, as it was parked (D-212). */
  heldForHours: number
  /** At least one line the shop can no longer fill. */
  isShort: boolean
}

export type ParkedSaleLine = {
  productId: string
  code: string
  name: string
  quantity: number
  discountPercent: number
  discountAmount: number
  /** It was parked as boxes, so it has to come back as boxes (D-210). */
  asPack: boolean
  piecesPerUnit: number
  /** "Box of 10", as the product is worded today. Null on a line parked as singles. */
  packDescription?: string
  /** What the line takes off a shelf. */
  pieces: number
  /** What the shop could supply for it now, counting this sale's own hold as its own (D-212). */
  availableNow: number
  /** Pieces missing. Nought means the shop can still fill it. */
  shortfall: number
}

export function useParkedSales(locationId: string) {
  return useQuery({
    queryKey: ['pos', 'parked', locationId],
    queryFn: ({ signal }) => api<ParkedSale[]>(`/api/pos/parked?locationId=${locationId}`, { signal }),
  })
}

export function useParkSale(locationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      registerSessionId: string
      customerId: string
      reference: string
      lines: { productId: string; quantity: number; discountPercent: number; discountAmount: number; asPack?: boolean }[]
      /** 2, 4 or 6 hours. Omitted, the shop's own default applies (D-212). */
      stockHoldHours?: number
    }) => api<ParkedSale>('/api/pos/parked', { method: 'POST', body: { locationId, ...body } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['pos', 'parked', locationId] }),
  })
}

export function useResumeSale(locationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, registerSessionId }: { id: string; registerSessionId: string }) =>
      api<ParkedSale>(`/api/pos/parked/${id}/resume`, { method: 'POST', body: { registerSessionId } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['pos', 'parked', locationId] }),
  })
}

export function useAbandonSale(locationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<ParkedSale[]>(`/api/pos/parked/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['pos', 'parked', locationId] }),
  })
}

/** A counter a cashier may open at. `openedBy` is who has it, so the answer is "go and see Grace". */
export type PosCounter = { id: string; name: string; openedBy?: string }

export type ApprovalKind = 'Discount' | 'Price' | 'Credit'
export type ApprovalNeed = { kind: ApprovalKind; productId?: string; reason: string; discountPercent?: number }

export type QuoteLine = {
  productId: string
  code: string
  name: string
  quantity: number
  unitPrice: number
  priceSource: string
  discountPercent: number
  discountAmount: number
  lineNet: number
  lineVat: number
  lineTotal: number
  vatRate: number
  /** In PIECES, as the shelf counts — a box line divides it by the pack size itself. */
  available: number
  /** Priced as a box rather than singly (D-210), so the till can match it to the right line. */
  asPack: boolean
  /** Loose pieces at the box's own rate (D-235). */
  atPackRate?: boolean
}

/**
 * A line the shop is short of.
 *
 * The figures travel rather than a sentence, because the till does two things a sentence cannot
 * support: it offers the quantity that would work, and it offers to find the rest somewhere else.
 */
export type QuoteShortage = {
  productId: string
  code: string
  name: string
  requested: number
  /** What is here, net of every hold including other counters'. Never negative. */
  available: number
}

export type SaleQuote = {
  lines: QuoteLine[]
  subTotal: number
  discountTotal: number
  vatTotal: number
  grandTotal: number
  vatExemptionCertificate?: string
  approvals: ApprovalNeed[]
  needsSomeoneElse: ApprovalNeed[]
  refusal?: string
  /** Lines the shop cannot hold enough of, with what is actually there (D-193). */
  shortages?: QuoteShortage[]
  /** The customer's loyalty membership (M18), when they have one. */
  loyaltyTier?: string
  loyaltyDiscountPercent: number
  loyaltyPoints?: number
}

/** A product as the till picks it: from the shelf, a scan, a search, or recent items. */
export type PickedProduct = {
  id: string
  code: string
  name: string
  retailPrice?: number
  imageUrl?: string
  /** What this shop can sell now, where the picker knows it (the shelf does). */
  available?: number
  /** Set only where the product is sold by the pack AND has a pack price (D-204). */
  packSize?: number
  packDescription?: string
  /** What a whole pack costs, for the shelf to show when the box is chosen (D-210). */
  packPrice?: number
  /** The unit the shelf was switched to when this was picked. Absent means a single. */
  asPack?: boolean
}

export type CartLine = {
  productId: string
  name: string
  code: string
  imageUrl?: string
  quantity: number
  /** The cashier types either a percentage or a shilling amount for the line. */
  discountPercent: number
  discountAmount: number
  /** Sold by the pack rather than singly (D-204): the quantity counts boxes, not pieces. */
  asPack?: boolean
  /** How many pieces are in one, where this can be sold by the pack at all. */
  packSize?: number
  /** "Box of 10", for the line's own toggle. */
  packDescription?: string
  /**
   * Loose pieces charged at what they cost inside the box (D-235).
   *
   * A third kind of line, not a flag on the second: a basket can hold a box, loose pieces at shelf
   * price and loose pieces at the box rate, and keyed as one they collapse into each other.
   */
  atPackRate?: boolean
}

/**
 * One basket line's identity: the product AND the unit it is being sold in (D-210).
 *
 * A basket may hold a box and a loose one of the same thing, at two different prices. Keyed on the
 * product alone they are one line, and the second one silently becomes more of the first — which is
 * the same correction the till's price map, the ERP's order handler and the storefront's cart each
 * needed (D-204, D-206, D-209).
 */
export const lineKey = (productId: string, asPack?: boolean, atPackRate?: boolean) =>
  `${productId}:${asPack === true ? 'pack' : atPackRate === true ? 'packrate' : 'single'}`

/** An M-Pesa tender carries the payment it came from (D-082); its code is the payment's, never typed. */
export type Tender = {
  mode: PaymentMode; amount: number; reference?: string; mpesaPaymentId?: string; mpesaCode?: string
  /** An exchange-credit tender spends an approved credit note (M17). */
  creditNoteId?: string; creditNoteCode?: string; creditRemaining?: number
}

export type SaleRequest = {
  customerId: string
  locationId: string
  registerSessionId?: string
  lines: { productId: string; quantity: number; discountPercent: number; discountAmount: number; asPack?: boolean; atPackRate?: boolean }[]
  payments: Tender[]
  approval?: { approverUserId: string; pin: string }
  approvalReason?: string
  customerOrderNumber?: string

  /** Ring this sale without VAT (D-222). The reason is required with it and is printed. */
  vatOff?: boolean
  vatOffReason?: string
}

export type SaleResult = {
  invoiceId: string
  code: string
  orderNumber: string
  subTotal: number
  vatTotal: number
  grandTotal: number
  changeGiven: number
  customerBalance: number
  pointsEarned: number
}

export type Approver = {
  id: string
  name: string
  role: string
  canApproveDiscount: boolean
  discountLimit?: number | null
  canOverridePrice: boolean
  canOverrideCredit: boolean
  /** May give somebody else's hold to the customer at the counter (D-198). */
  canReleaseHold: boolean
  /** Whether a code can be texted to them at all — false means no phone on file (D-240). */
  canBeTexted: boolean
}

/** What came back from asking for a code. Never the code itself: it only exists on the phone. */
export type ApprovalCodeSent = { approverName: string; phoneHint: string; expiresAt: string }

/**
 * Asks the server to text a one-time approval code to somebody (D-240).
 *
 * The figures are only what the approver reads in the message. The server re-checks the real sale
 * against them when the code is spent, so there is nothing to gain by sending the wrong ones.
 */
/**
 * Customers matching whatever the cashier typed — a name, an account number or a phone (D-243).
 *
 * Debounced in the component rather than here, and disabled under two characters so pressing the
 * box by accident does not fetch the first ten customers in the book.
 */
export function useCustomerSearch(term: string) {
  const q = term.trim()

  return useQuery({
    queryKey: ['pos', 'customer-search', q],
    queryFn: ({ signal }) => api<PosCustomer[]>(`/api/pos/customers/search?q=${encodeURIComponent(q)}`, { signal }),
    enabled: q.length >= 2,
    placeholderData: keepPreviousData,
  })
}

export function useRequestApprovalCode() {
  return useMutation({
    mutationFn: (body: {
      locationId: string
      approverUserId: string
      purpose: 'Discount' | 'PriceOverride' | 'CreditOverride' | 'VatWaiver' | 'HoldRelease'
      discountPercent?: number
      saleValue?: number
    }) => api<ApprovalCodeSent>('/api/pos/approvals/code', { method: 'POST', body }),
  })
}

export type Receipt = {
  invoiceId: string
  invoiceCode: string
  /** The customer's LPO, on a corporate account's sale. */
  customerOrderNumber?: string
  orderNumber?: string
  issuedAt: string
  companyName: string
  companyLegalName?: string
  companyKraPin?: string
  companyVatNumber?: string
  /** D-144: false for a shop that is not VAT-registered — a receipt, not a tax invoice. */
  sellerVatRegistered?: boolean
  /** D-144: the shop's own till or paybill, for customers to pay into. */
  paymentInstructions?: string
  companyAddress?: string
  companyPhone?: string
  locationName: string
  cashierName?: string
  customerId: string
  customerName: string
  /** As the receipt should PRINT it — masked where the company asked for that (D-246). */
  customerPhone?: string
  /** The real number, for sending them their receipt. Never printed. */
  customerPhoneForSending?: string
  customerEmail?: string
  customerKraPin?: string
  customerIsWalkIn: boolean
  saleType: 'Cash' | 'Credit'
  /** taxType is KRA's eTIMS letter: A exempt, B 16%, C zero-rated, D non-VAT, E 8%. */
  lines: { code: string; name: string; quantity: number; unitPrice: number; discountAmount: number; lineNet: number; lineVat: number; lineTotal: number; vatRate: number; taxType: string }[]
  subTotal: number
  discountTotal: number
  vatTotal: number
  grandTotal: number
  vatSummary: { rate: number; taxable: number; vat: number }[]
  payments: { mode: PaymentMode; amount: number; reference?: string }[]
  changeGiven: number
  vatExemptionCertificate?: string
  /** Why VAT came off at the till, where no certificate explains it (D-224). */
  vatWaiverReason?: string
  pricesIncludeVat: boolean
  footerText?: string
  /** What KRA's control unit said about it (M16); absent before eTIMS was switched on. */
  etims?: { status: 'Pending' | 'Transmitted' | 'Failed'; invoiceNumber?: string; controlUnitId?: string; receiptSignature?: string; qrPayload?: string; transmittedAt?: string; isSandbox: boolean }
  dueDate?: string
}

export const posKeys = {
  locations: ['pos', 'locations'] as const,
  context: (locationId: string) => ['pos', 'context', locationId] as const,
  approvers: (locationId: string) => ['pos', 'approvers', locationId] as const,
  holds: (locationId: string, productId: string) => ['pos', 'holds', locationId, productId] as const,
  receipt: (id: string) => ['pos', 'receipt', id] as const,
}

export function usePosLocations() {
  return useQuery({ queryKey: posKeys.locations, queryFn: ({ signal }) => api<PosLocation[]>('/api/pos/locations', { signal }) })
}

export function usePosContext(locationId: string) {
  return useQuery({
    queryKey: posKeys.context(locationId),
    queryFn: ({ signal }) => api<PosContext>(`/api/pos/context?locationId=${locationId}`, { signal }),
    enabled: !!locationId,
  })
}

/**
 * The basket, priced by the server. Re-asked whenever the basket changes; the
 * previous answer stays on screen meanwhile so the totals never blank out.
 */
export function useQuote(request: SaleRequest | null) {
  return useQuery({
    queryKey: ['pos', 'quote', request],
    queryFn: ({ signal }) => api<SaleQuote>('/api/pos/quote', { method: 'POST', body: request, signal }),
    enabled: !!request && request.lines.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 0,
  })
}

export function useApprovers(locationId: string, enabled: boolean) {
  return useQuery({
    queryKey: posKeys.approvers(locationId),
    queryFn: ({ signal }) => api<Approver[]>(`/api/pos/approvers?locationId=${locationId}`, { signal }),
    enabled,
  })
}

/** One hold standing between this shop's shelf and the customer at the counter (D-198). */
export type StockHold = {
  id: string
  purpose: string
  quantity: number
  heldAt: string
  expiresAt: string
  /** As a cashier would say it: "A basket at Counter 2", "Parked sale “Mary”". */
  what: string
  who?: string
  customerName?: string
  mayRelease: boolean
  /** Why it cannot be taken — which usually says what to do instead. */
  whyNot?: string
  /** What somebody now owes the person who was waiting for it. */
  tellTheCustomer?: string
}

/**
 * Who is holding this product here.
 *
 * Asked only when a line comes up short — the dialogue that asks it is the one the shortage opens —
 * so an ordinary scan sends nothing. The till's own session is named so its own basket is left out:
 * the quote already adds that back before deciding what may be sold.
 */
export function useProductHolds(locationId: string, productId: string, registerSessionId: string) {
  return useQuery({
    queryKey: posKeys.holds(locationId, productId),
    queryFn: ({ signal }) =>
      api<StockHold[]>(
        `/api/pos/holds?locationId=${locationId}&productId=${productId}&registerSessionId=${registerSessionId}`,
        { signal },
      ),
    enabled: !!productId,
  })
}

export function useReleaseHold(locationId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason, approverUserId, pin }: {
      id: string
      reason: string
      approverUserId: string
      pin: string
    }) =>
      api<StockHold>(`/api/pos/holds/${id}/release`, {
        method: 'POST',
        body: { locationId, reason, approverUserId, pin },
      }),
    onSuccess: () => {
      // The shelf has changed for everybody, not only for this till.
      void client.invalidateQueries({ queryKey: ['pos', 'holds', locationId] })
      void client.invalidateQueries({ queryKey: ['products', 'list'] })
    },
  })
}

/**
 * Where a customer's credit stands (D-199).
 *
 * `committed` is on open orders and NOT part of `outstanding`: a customer will dispute a balance that
 * mixes what they owe with what they have only ordered, and they would be right to.
 */
export type CreditPosition = {
  creditLimit: number
  outstanding: number
  committed: number
  available: number
  openOrders: number
}

/** An order taken at the counter on a customer's account (D-199). */
export type AccountOrder = {
  id: string
  orderNumber: string
  status: string
  customerId: string
  customerName: string
  takenAt: string
  /** How long the proforma's prices stand. Nothing is held in the meantime. */
  validUntil: string
  lpo?: string
  forCollection: boolean
  approvedBy?: string
  approvalReason?: string
  total: number
  lines: AccountOrderLine[]
}

export type AccountOrderLine = {
  productId: string
  code: string
  name: string
  quantity: number
  unitPrice: number
  discountPercent: number
  discountAmount: number
  lineTotal: number
  /** On the shelf now. The order holds none of it. */
  available: number
}

export function useCreditPosition(customerId: string | null) {
  return useQuery({
    queryKey: ['pos', 'credit', customerId ?? ''],
    queryFn: ({ signal }) => api<CreditPosition>(`/api/pos/credit/${customerId}`, { signal }),
    enabled: !!customerId,
  })
}

export function useAccountOrders(locationId: string, customerId: string | null) {
  return useQuery({
    queryKey: ['pos', 'orders', locationId, customerId ?? ''],
    queryFn: ({ signal }) =>
      api<AccountOrder[]>(`/api/pos/orders?customerId=${customerId}&locationId=${locationId}`, { signal }),
    enabled: !!customerId,
  })
}

export function useTakeAccountOrder(locationId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (body: {
      customerId: string
      lines: { productId: string; quantity: number; discountPercent: number; discountAmount: number; asPack?: boolean; atPackRate?: boolean }[]
      customerOrderNumber?: string
      forCollection: boolean
      approverUserId?: string
      pin?: string
      approvalReason?: string
    }) =>
      api<AccountOrder>('/api/pos/orders', {
        method: 'POST',
        body: {
          locationId,
          customerId: body.customerId,
          lines: body.lines,
          customerOrderNumber: body.customerOrderNumber,
          forCollection: body.forCollection,
          approval: body.approverUserId ? { approverUserId: body.approverUserId, pin: body.pin } : undefined,
          approvalReason: body.approvalReason,
        },
      }),
    onSuccess: (order) => {
      // Their credit has moved, and so has the list of what they have open.
      void client.invalidateQueries({ queryKey: ['pos', 'credit', order.customerId] })
      void client.invalidateQueries({ queryKey: ['pos', 'orders', locationId] })
    },
  })
}

/** The customer confirmed the proforma: the goods come off the shelf for three working days (D-200). */
export function useAcceptAccountOrder(locationId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, approverUserId, pin, approvalReason }: {
      id: string
      approverUserId?: string
      pin?: string
      approvalReason?: string
    }) =>
      api<AccountOrder>(`/api/pos/orders/${id}/accept`, {
        method: 'POST',
        body: {
          locationId,
          approval: approverUserId ? { approverUserId, pin } : undefined,
          approvalReason,
        },
      }),
    onSuccess: (order) => {
      void client.invalidateQueries({ queryKey: ['pos', 'orders', locationId] })
      void client.invalidateQueries({ queryKey: ['pos', 'credit', order.customerId] })
      // The shelf has moved for everybody.
      void client.invalidateQueries({ queryKey: ['products', 'list'] })
    },
  })
}

/** The goods are going out: the tax invoice, the receipt and the debtor entry (D-200). */
export function useInvoiceAccountOrder(locationId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, registerSessionId, approverUserId, pin, approvalReason }: {
      id: string
      registerSessionId?: string
      approverUserId?: string
      pin?: string
      approvalReason?: string
    }) =>
      api<SaleResult>(`/api/pos/orders/${id}/invoice`, {
        method: 'POST',
        body: {
          registerSessionId,
          approval: approverUserId ? { approverUserId, pin } : undefined,
          approvalReason,
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['pos', 'orders', locationId] })
      void client.invalidateQueries({ queryKey: ['pos', 'credit'] })
      void client.invalidateQueries({ queryKey: ['products', 'list'] })
    },
  })
}

export function useCancelAccountOrder(locationId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api<AccountOrder>(`/api/pos/orders/${id}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: (order) => {
      void client.invalidateQueries({ queryKey: ['pos', 'orders', locationId] })
      void client.invalidateQueries({ queryKey: ['pos', 'credit', order.customerId] })
      void client.invalidateQueries({ queryKey: ['products', 'list'] })
    },
  })
}

export function useReceipt(id: string | null) {
  return useQuery({
    queryKey: posKeys.receipt(id ?? ''),
    queryFn: ({ signal }) => api<Receipt>(`/api/pos/receipts/${id}`, { signal }),
    enabled: !!id,
  })
}

/**
 * Where else a product is. Only asked when the shop is short, so it is a mutation rather than a
 * query: nothing should go looking across every branch because a cashier scanned something.
 */
export function useStockElsewhere(locationId: string) {
  return useMutation({
    mutationFn: ({ productId, quantity }: { productId: string; quantity: number }) =>
      api<StockElsewhere[]>(
        `/api/pos/elsewhere?productId=${productId}&locationId=${locationId}&quantity=${quantity}`),
  })
}

/** Books goods this shop has not got. Takes no money — the customer pays on collection. */
export function useBookCollection(locationId: string) {
  return useMutation({
    mutationFn: (body: { customerId: string; fromLocationId: string; productId: string; quantity: number }) =>
      api<CollectionOrderResult>('/api/pos/collection-orders', { method: 'POST', body: { locationId, ...body } }),
  })
}

export function useOpenSession(locationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ openingFloat, counterId }: { openingFloat: number; counterId?: string }) =>
      api<RegisterSession>('/api/pos/sessions', { method: 'POST', body: { locationId, openingFloat, counterId } }),
    onSuccess: () => void client.invalidateQueries({ queryKey: posKeys.context(locationId) }),
  })
}

/**
 * Closes the session. Deliberately does **not** refresh the till on success: the
 * refreshed till has no open session and replaces itself with "open the register",
 * unmounting the result — so the cashier never saw their variance. The caller
 * refreshes once the result has been read (see `useRefreshTill`).
 */
export function useCloseSession() {
  return useMutation({
    mutationFn: ({ id, countedCash, note }: { id: string; countedCash: number; note?: string }) =>
      api<RegisterSession>(`/api/pos/sessions/${id}/close`, { method: 'POST', body: { countedCash, note } }),
  })
}

export function useRefreshTill(locationId: string) {
  const client = useQueryClient()
  return () => void client.invalidateQueries({ queryKey: posKeys.context(locationId) })
}

export function useCaptureCustomer() {
  return useMutation({
    mutationFn: (input: { phone: string; name?: string; email?: string; locationId: string }) =>
      api<PosCustomer>('/api/pos/customers', { method: 'POST', body: input }),
  })
}

export function useSell(locationId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ request, idempotencyKey }: { request: SaleRequest; idempotencyKey: string }) =>
      api<SaleResult>('/api/pos/sales', { method: 'POST', body: request, idempotencyKey }),
    // The session's takings changed; refresh them for the register panel.
    onSuccess: () => void client.invalidateQueries({ queryKey: posKeys.context(locationId) }),
  })
}

export function useSetPin() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; pin: string }) =>
      api<void>('/api/auth/pin', { method: 'POST', body: input }),
  })
}

export const tenderLabels: Record<PaymentMode, string> = {
  Cash: 'Cash',
  Mpesa: 'M-Pesa',
  EquityBank: 'Equity Bank',
  DtbBank: 'DTB Bank',
  AbsaBank: 'Absa Bank',
  SidianBank: 'Sidian Bank',
  Card: 'Card',
  CreditSale: 'On account',
  Cheque: 'Cheque',
  ExchangeCredit: 'Exchange credit',
  LoyaltyPoints: 'Loyalty points',
}

/** Tenders that carry a reference the receipt must show. */
export const needsReference: PaymentMode[] = ['EquityBank', 'SidianBank', 'DtbBank', 'AbsaBank', 'Card', 'Cheque']
